import { z } from "zod";

/**
 * A help article the assistant opened while answering — rendered as a link out to the public help
 * center. The URL comes from the article record upstream, never from the answer text, so it always
 * resolves; the title is in the article's own language, which need not match the answer's.
 */
export const HelpArticleSourceSchema = z.object({
	slug: z.string(),
	title: z.string(),
	url: z.string(),
});

/** Response of POST /help-center/chat. */
export const HelpChatResponseSchema = z.object({
	answer: z.string(),
	sources: HelpArticleSourceSchema.array(),
	/** Conversation this turn was saved to — sent back on the next turn to keep the thread. */
	conversationId: z.number().optional(),
	/** Persisted assistant message id — the target for thumbs up/down feedback. */
	messageId: z.number().optional(),
	/** True when the assistant couldn't resolve the question — surfaces the "create ticket" CTA. */
	canEscalate: z.boolean().optional().default(false),
});

/** Response of POST /help-center/support-ticket. */
export const SupportTicketResponseSchema = z.object({
	success: z.boolean(),
});

export type HelpArticleSource = z.infer<typeof HelpArticleSourceSchema>;
export type HelpChatResponse = z.infer<typeof HelpChatResponseSchema>;
export type SupportTicketResponse = z.infer<typeof SupportTicketResponseSchema>;

/** One prior conversation turn replayed to the agent for short-term memory. */
export interface HelpChatHistoryTurn {
	role: "user" | "assistant";
	content: string;
}

/** The page the user is on when they ask — route *pattern* only, never resolved ids. */
export interface HelpChatPageContext {
	routePattern?: string;
	label?: string;
	activeTab?: string;
}

/** Request body for POST /help-center/chat. */
export interface HelpChatRequest {
	message: string;
	pageContext?: HelpChatPageContext;
	history?: HelpChatHistoryTurn[];
	/** The user's UI language (i18next code) — corpus locale + the answer language. */
	language?: string;
	/** Id of the conversation to continue; omit on the first turn (the server opens a new one). */
	conversationId?: number;
}

/** Request body for POST /help-center/support-ticket. The user's identity is added server-side. */
export interface SupportTicketRequest {
	/** Short subject; the server falls back to a generic one when omitted. */
	subject?: string;
	message: string;
	pageContext?: HelpChatPageContext;
	/** The chat transcript that led to the ticket, forwarded to support for context. */
	transcript?: HelpChatHistoryTurn[];
}
